import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Send, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { TutorAvatar, DEFAULT_TUTOR_NAME } from '@/components/tutor/TutorAvatar';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { ApiError } from '@/lib/api';
import {
  askStudyTutor,
  getStudySession,
  getStudyTutorPrice,
  selectStudyPdfPage,
  startStudySession,
  type StudySession,
  type StudyTutorResult,
} from './studyApi';
import { StudySourceTextCheck } from './StudySourceTextCheck';
import { StudyPdfTutorSetup } from './StudyPdfTutorSetup';
import { studyAccessMessage, tutorStateMessage } from './studyAccessMessages';
import { subjectAvailability, useStudyAvailability } from './studyAvailability';
import { StudyContextTools } from './StudyContextTools.tsx';

const schema = z.object({
  question: z.string().trim().min(1, 'Write a question first.').max(1200),
});
type Form = z.infer<typeof schema>;
interface Props {
  documentId: string;
  pageNumber: number;
  fileName: string;
  active: boolean;
  ready: boolean;
  preparePage: () => Promise<Blob>;
  onBusyChange: (busy: boolean) => void;
}

function message(error: unknown) {
  return (
    studyAccessMessage(error) ??
    (error instanceof Error ? error.message : 'Tutor could not connect. Please try again.')
  );
}

/** The original PDF page is attached through the existing source-bound Study Session contract. */
export function StudyPdfTutorPanel({
  documentId,
  pageNumber,
  fileName,
  active,
  ready,
  preparePage,
  onBusyChange,
}: Props) {
  const client = useQueryClient();
  const owner = useAuthStore(() => getKidSub());
  const ownerDocument = `${owner}:${documentId}`;
  const scope = `${ownerDocument}:${pageNumber}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [bindingSource, setBindingSource] = useState<{ ownerDocument: string; id: string } | null>(
    null,
  );
  const sessionId = bindingSource?.ownerDocument === ownerDocument ? bindingSource.id : null;
  const [delivered, setDelivered] = useState<{
    ownerDocument: string;
    reply: StudyTutorResult;
  } | null>(null);
  const [prepared, setPrepared] = useState<{ scope: string; page: number; image: Blob } | null>(
    null,
  );
  const lastReply = delivered?.ownerDocument === ownerDocument ? delivered.reply : null;
  const conversation = useRef<HTMLDivElement>(null);
  const currentPage = useRef(pageNumber);
  currentPage.current = pageNumber;
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const form = useForm<Form>({ resolver: zodResolver(schema), defaultValues: { question: '' } });
  const { reset } = form;
  useEffect(() => {
    reset();
    setDelivered(null);
  }, [ownerDocument, reset]);
  const isCurrent = (requestScope: string) =>
    live.current && currentScope.current === requestScope && getKidSub() === owner;
  const price = useQuery({
    queryKey: ['study-tutor-price', owner],
    queryFn: getStudyTutorPrice,
    enabled: active,
    retry: false,
  });
  const availability = useStudyAvailability(active);
  const key = ['study-pdf-tutor-session', owner, documentId, sessionId];
  const session = useQuery({
    queryKey: key,
    queryFn: async () => {
      const saved = await getStudySession(sessionId!);
      if (getKidSub() !== owner || saved.id !== sessionId || saved.document.id !== documentId)
        throw new Error('This saved conversation does not belong to the current source.');
      return saved;
    },
    enabled: Boolean(sessionId) && active,
    retry: false,
  });
  const binding = useMutation({
    mutationFn: async () => {
      const page = pageNumber;
      const requestScope = scope;
      const image = await preparePage();
      if (!isCurrent(requestScope))
        throw new Error('The source changed. Prepare the current page.');
      const current = sessionId
        ? await getStudySession(sessionId)
        : await startStudySession(documentId);
      if (!isCurrent(requestScope))
        throw new Error('The source changed. Prepare the current page.');
      // Only the plain document session may be repurposed. Question/notebook sessions keep their source.
      const canReuse =
        current.status === 'active' &&
        !current.analysis_question &&
        !current.notebook_source &&
        (!current.messages.length || current.document.selected_page?.page_number === page);
      // Preparing a different page must not mutate the source of saved replies.
      // Stage it locally; only the learner's next Ask starts a fresh conversation.
      if (!canReuse) return { bound: current, page, requestScope, image };
      const target = current;
      if (!isCurrent(requestScope))
        throw new Error('The source changed. Prepare the current page.');
      const bound = await selectStudyPdfPage(target.id, page, image);
      return { bound, page, requestScope, image: null };
    },
    onSuccess: ({ bound, page, requestScope, image }) => {
      if (!isCurrent(requestScope) || currentPage.current !== page) return;
      setBindingSource({ ownerDocument, id: bound.id });
      setPrepared(image ? { scope: requestScope, page, image } : null);
      setDelivered(null);
      client.setQueryData(['study-pdf-tutor-session', owner, documentId, bound.id], bound);
    },
  });
  const data = session.data;
  const selectedPage = data?.document.selected_page?.page_number;
  const bound = Boolean(
    prepared?.scope === scope ||
    (data &&
      selectedPage === pageNumber &&
      data.document.tutor_source_ready &&
      !data.document.focused_region),
  );
  const policy = subjectAvailability(availability.data, data?.subject.subject_key ?? '');
  const paused = policy && policy.tutor.state !== 'available' ? tutorStateMessage(policy) : null;
  const needsText =
    availability.data?.policy.require_source_confirmation === true && !data?.source_confirmation;
  const needsGuidance = Boolean(data?.subject_tutor && data.subject_tutor.status !== 'published');
  const requests = useRef(new Map<string, string>());
  const ask = useMutation({
    mutationFn: async (question: string) => {
      if (
        !data ||
        !bound ||
        !price.data ||
        !availability.data ||
        price.isError ||
        availability.isError ||
        paused ||
        needsText ||
        needsGuidance ||
        subjectOff
      )
        throw new Error('Prepare this page and check Tutor access first.');
      let target = data;
      const requestScope = scope;
      if (prepared?.scope === requestScope) {
        const fresh = await startStudySession(documentId, 'understand', { fresh: true });
        if (!isCurrent(requestScope)) throw new Error('The source changed. Your question is kept.');
        target = await selectStudyPdfPage(fresh.id, prepared.page, prepared.image);
        if (!isCurrent(requestScope)) throw new Error('The source changed. Your question is kept.');
        setBindingSource({ ownerDocument, id: target.id });
        setPrepared(null);
        setDelivered(null);
        client.setQueryData(['study-pdf-tutor-session', owner, documentId, target.id], target);
        if (availability.data.policy.require_source_confirmation && !target.source_confirmation)
          throw new Error(
            'Check this new page’s source text before sending. No AI started and no Stars were taken.',
          );
      }
      const fingerprint = JSON.stringify({
        id: target.id,
        pageNumber,
        question,
        source: data.source_confirmation,
        stars: price.data.stars,
      });
      let id = requests.current.get(fingerprint);
      if (!id) {
        id = crypto.randomUUID();
        requests.current.set(fingerprint, id);
      }
      const reply = await askStudyTutor(target.id, {
        question,
        hint_level: 0,
        expected_stars: price.data.stars,
        idempotency_key: id,
      });
      return { reply, requestScope, targetId: target.id };
    },
    onSuccess: ({ reply, requestScope, targetId }) => {
      if (!isCurrent(requestScope)) return;
      setDelivered({ ownerDocument, reply });
      form.reset();
      void client.invalidateQueries({
        queryKey: ['study-pdf-tutor-session', owner, documentId, targetId],
      });
    },
    onError: () => {
      void availability.refetch();
      void price.refetch();
    },
  });
  const question = form.watch('question');
  useEffect(() => {
    onBusyChange(binding.isPending || ask.isPending);
  }, [binding.isPending, ask.isPending, onBusyChange]);
  useEffect(() => {
    const pane = conversation.current;
    if (pane && (data?.messages.length || lastReply?.id || ask.isPending))
      pane.scrollTop = pane.scrollHeight;
  }, [data?.messages.length, lastReply?.id, ask.isPending]);
  const subjectOff = availability.data?.study_visible === false || policy?.enabled === false;
  const canAsk =
    ready &&
    bound &&
    data?.status === 'active' &&
    Boolean(price.data && availability.data) &&
    !session.isError &&
    !price.isError &&
    !availability.isError &&
    !paused &&
    !needsText &&
    !needsGuidance &&
    !subjectOff &&
    !binding.isPending;
  const confirmed = (updated: StudySession) => client.setQueryData(key, updated);
  const savedReply = lastReply
    ? data?.messages.find(
        (item) => item.role === 'assistant' && item.id === lastReply.assistant_message_id,
      )
    : data?.messages.filter((item) => item.role === 'assistant').at(-1);
  const canLinkReply = Boolean(savedReply);
  return (
    <section className="flex min-h-0 flex-1 flex-col" aria-label="PDF Tutor">
      <header className="flex shrink-0 items-center gap-3 border-b border-hairline bg-wash-sky/50 px-4 py-3">
        <TutorAvatar size="small" expression={ask.isPending ? 'thinking' : 'friendly'} />
        <div>
          <h3 className="text-sm font-extrabold text-ink">Ask {DEFAULT_TUTOR_NAME}</h3>
          <p className="mt-0.5 text-xs text-slate2">AI Tutor · understand this PDF page</p>
        </div>
      </header>
      <div ref={conversation} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        <div className="rounded-2xl border border-hairline bg-canvas p-3">
          <p className="flex items-center gap-2 text-xs font-bold text-ink">
            <FileText className="h-4 w-4 shrink-0 text-brand-sky" />
            <span className="truncate">{fileName}</span>
          </p>
          <p className="mt-2 text-xs leading-relaxed text-slate2">
            {bound
              ? 'Tutor is using page ' + pageNumber + '. Your annotations are not sent.'
              : 'Attach page ' +
                pageNumber +
                ' before asking. Tutor reads one selected page at a time.'}
          </p>
          {!bound && (
            <button
              type="button"
              disabled={!ready || binding.isPending || ask.isPending}
              onClick={() => binding.mutate()}
              className="btn-pill-secondary mt-3 min-h-11 w-full gap-2 text-xs"
            >
              {binding.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {binding.isPending
                ? 'Preparing this page…'
                : 'Use page ' + pageNumber + ' with Tutor'}
            </button>
          )}
        </div>
        {binding.error && (
          <p role="alert" className="rounded-xl bg-wash-coral p-3 text-xs text-ink">
            {message(binding.error)}
          </p>
        )}
        {binding.error instanceof ApiError &&
          binding.error.code === 'STUDY_DOCUMENT_NOT_ORGANISED' && (
            <StudyPdfTutorSetup documentId={documentId} onReady={() => binding.mutate()} />
          )}
        {subjectOff && (
          <p role="status" className="rounded-xl bg-wash-sunshine p-3 text-xs text-ink">
            Study or this subject is unavailable for your account. Your saved PDF annotations still
            work.
          </p>
        )}
        {session.isError && (
          <div role="alert" className="rounded-xl bg-wash-coral p-3 text-xs text-ink">
            The conversation could not load.
            <button
              type="button"
              className="btn-pill-secondary mt-2 min-h-11"
              onClick={() => void session.refetch()}
            >
              Reload conversation
            </button>
          </div>
        )}
        {bound && data && (
          <details
            open={needsText}
            className="rounded-2xl border border-hairline bg-canvas-pure p-3"
          >
            <summary className="min-h-11 cursor-pointer py-3 text-xs font-bold text-ink">
              Check source text {needsText ? '(required)' : '(optional)'}
            </summary>
            <StudySourceTextCheck
              key={data.id + ':' + selectedPage}
              sessionId={data.id}
              confirmation={data.source_confirmation}
              required={availability.data?.policy.require_source_confirmation === true}
              onConfirmed={confirmed}
            />
          </details>
        )}
        {paused && (
          <p role="status" className="rounded-xl bg-wash-sunshine p-3 text-xs text-ink">
            {paused}
          </p>
        )}
        {needsGuidance && (
          <p role="status" className="rounded-xl bg-wash-sunshine p-3 text-xs text-ink">
            Tutor guidance for this subject is not published yet. Your PDF and notes still work.
          </p>
        )}
        {!data?.messages.length && !lastReply && (
          <div className="p-2 text-sm leading-relaxed text-ink-soft">
            <p className="font-bold text-ink">Let's make sense of this page.</p>
            <p className="mt-2 text-xs text-slate2">
              Ask about a term, the question, or the main idea. Tutor can make mistakes; check the
              PDF.
            </p>
            <button
              type="button"
              disabled={!canAsk || ask.isPending}
              onClick={() =>
                form.setValue('question', 'Help me understand the main idea on this page.')
              }
              className="mt-3 min-h-11 rounded-xl border border-hairline bg-canvas-pure px-3 text-left text-xs font-bold text-ink"
            >
              Help me understand this page
            </button>
          </div>
        )}
        {data?.messages.length ? (
          <p className="text-[11px] font-bold text-slate2">Conversation · page {selectedPage}</p>
        ) : null}
        {data?.messages.map((item) => (
          <article
            key={item.id}
            className={
              item.role === 'user'
                ? 'ml-4 rounded-2xl bg-ink p-3 text-white'
                : 'mr-2 rounded-2xl bg-surface p-3 text-ink'
            }
          >
            <p className="mb-1 text-[11px] font-bold">
              {item.role === 'assistant' ? DEFAULT_TUTOR_NAME : 'You'}
            </p>
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
              {item.content}
            </p>
          </article>
        ))}
        {lastReply &&
          !data?.messages.some(
            (item) => item.role === 'assistant' && item.content === lastReply.reply,
          ) && (
            <article
              aria-label="Latest Tutor reply"
              className="rounded-2xl bg-surface p-3 text-sm leading-relaxed text-ink"
            >
              <p className="mb-1 text-xs font-bold">{DEFAULT_TUTOR_NAME}</p>
              <p className="whitespace-pre-wrap">{lastReply.reply}</p>
            </article>
          )}
        {!ask.isPending && data && data.goal !== 'assessment' && canLinkReply && (
          <StudyContextTools
            compact
            disabled={ask.isPending || Boolean(question?.trim())}
            context={{
              subjectKey: data.subject.subject_key ?? '',
              label: `${fileName} · Page ${selectedPage ?? pageNumber}`,
              text:
                lastReply?.reply ??
                data.messages.filter((item) => item.role === 'assistant').at(-1)?.content ??
                '',
              returnTo: `/learn/study/sessions/${encodeURIComponent(data.id)}?message=${encodeURIComponent(savedReply!.id)}`,
            }}
          />
        )}
        {ask.isPending && (
          <p role="status" className="flex items-center gap-2 text-xs text-slate2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Tutor is reading your page…
          </p>
        )}
        {ask.error && (
          <p role="alert" className="rounded-xl bg-wash-coral p-3 text-xs text-ink">
            {message(ask.error)} Your question stays here for retry.
          </p>
        )}
      </div>
      <form
        onSubmit={form.handleSubmit((values) => ask.mutate(values.question))}
        className="shrink-0 border-t border-hairline bg-canvas-pure p-3"
      >
        {(!price.data || !availability.data) && (
          <p role="status" className="mb-2 text-xs text-slate2">
            {price.isError || availability.isError
              ? 'Tutor access or price could not load.'
              : 'Checking Tutor access and price…'}
            {(price.isError || availability.isError) && (
              <button
                type="button"
                className="ml-1 min-h-11 font-bold underline"
                onClick={() => {
                  void price.refetch();
                  void availability.refetch();
                }}
              >
                Retry Tutor access
              </button>
            )}
          </p>
        )}
        <p className="mb-2 text-[11px] leading-relaxed text-slate2">
          {price.data
            ? price.data.stars +
              '★ per message · PDF page ' +
              pageNumber +
              (data?.source_confirmation ? ' + confirmed text' : '')
            : 'No AI request is sent until you choose Send.'}
        </p>
        <label className="sr-only" htmlFor="pdf-tutor-question">
          Ask about this PDF
        </label>
        <textarea
          id="pdf-tutor-question"
          {...form.register('question')}
          rows={2}
          maxLength={1200}
          disabled={ask.isPending}
          placeholder={
            bound ? 'What would you like to understand?' : 'Attach the current page above first'
          }
          className="block max-h-28 min-h-20 w-full resize-y rounded-xl border border-hairline bg-canvas p-3 text-sm text-ink focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate2">
            {!bound
              ? 'Attach the current page first'
              : needsGuidance
                ? 'Waiting for published subject guidance'
                : needsText
                  ? 'Confirm the source text first'
                  : 'Understanding help · original page only'}
          </p>
          <button
            type="submit"
            disabled={!canAsk || ask.isPending || !question.trim()}
            className="btn-pill-primary min-h-11 gap-2 px-4 text-xs"
          >
            <Send className="h-4 w-4" />
            {ask.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {form.formState.errors.question && (
          <p role="alert" className="mt-1 text-xs text-danger-600">
            {form.formState.errors.question.message}
          </p>
        )}
      </form>
    </section>
  );
}
